import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { isAxiosError } from "axios";
import { v4 } from "uuid";

import { NodeBE } from "src/api/flowTypes";
import { useFloatingWindowsActions } from "src/base-components/FloatingWindow/hooks";
import {
  FeatureQueryConversationContext,
  NodeConversationContext,
  ToolUse,
} from "src/clients/copilot";
import {
  conversationKey,
  useAskCopilot,
  useConversation,
  useFetchCompletion,
} from "src/copilot/queries";
import { queryClient } from "src/queryClient";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { logger } from "src/utils/logger";

type CopilotContext =
  | (Omit<NodeConversationContext, "node_definition"> & {
      node_definition: NodeBE;
    })
  | FeatureQueryConversationContext;

type UseCopilotConversationParams = {
  conversationId: string;
  windowId: string;
  nodeId?: string;
  versionId?: string;
  resetConversationId: () => void;
};

export const useCopilotConversation = ({
  conversationId,
  windowId,
  nodeId,
  versionId,
  resetConversationId,
}: UseCopilotConversationParams) => {
  const [completionId, setCompletionId] = useState<string>(v4());
  const [indexToAnimate, setIndexToAnimate] = useState<number>(-1);
  const [prompt, setPrompt] = useState<string>("");
  const { close: closeWindow } = useFloatingWindowsActions();
  const { workspace } = useWorkspaceContext();
  const baseUrl = workspace.base_url ?? "";

  const listRef = useRef<HTMLUListElement>(null);

  const {
    mutateAsync: askCopilot,
    isPending: isAskCopilotLoading,
    isError: isAskCopilotError,
    failureCount: askCopilotFailureCount,
    reset: resetAskCopilot,
    variables,
  } = useAskCopilot({
    conversationId,
    baseUrl,
    versionId,
  });

  const {
    mutateAsync: fetchCompletion,
    isPending: isCompletionLoading,
    isError: isCompletionError,
    failureCount: completionFailureCount,
  } = useFetchCompletion(conversationId);

  const { data } = useConversation({
    conversationId,
  });

  const messages = data?.messages ?? [];
  const isLoading = isAskCopilotLoading || isCompletionLoading;
  const isError = isAskCopilotError || isCompletionError;
  const failureCount = askCopilotFailureCount + completionFailureCount;

  useLayoutEffect(() => {
    if (listRef.current) {
      listRef.current.children[
        listRef.current.children.length - 1
      ].scrollIntoView({ behavior: "smooth" });
    }
  }, [messages.length]);

  useEffect(() => {
    setIndexToAnimate(-1);
  }, [conversationId]);

  const handleClose = () => {
    closeWindow(windowId);
    setPrompt("");
    // setting the index to prevent any message from animating when
    // reopening the copilot window
    setIndexToAnimate(-1);
  };

  const onSendMessage = async (
    prompt: string | null,
    context: CopilotContext,
    toolApproval?: ToolUse,
  ) => {
    try {
      setIndexToAnimate(messages.length + 1);

      await askCopilot({
        nodeId,
        completionId,
        message: prompt,
        toolApproval,
        context,
      });
      setCompletionId(v4());
    } catch (e) {
      logger.error(e);

      // if the request failed due to a 503 timeout, we need start polling for completion
      if (isAxiosError(e)) {
        resetAskCopilot();
        try {
          await fetchCompletion({
            completionId,
            baseUrl,
            toolApproval,
          });
        } catch (e) {
          logger.error(e);
        }

        setCompletionId(v4());
      }
    }
  };

  const handleReset = () => {
    queryClient.removeQueries({
      queryKey: conversationKey(conversationId),
    });
    resetAskCopilot();
    setPrompt("");
    resetConversationId();
  };

  return {
    askCopilot,
    resetAskCopilot,
    variables,
    fetchCompletion,
    messages,
    isLoading,
    isError,
    failureCount,
    data,
    handleClose,
    onSendMessage,
    handleReset,
    indexToAnimate,
    listRef,
    prompt,
    setPrompt,
  };
};
